import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Locale } from "./i18n";
import { cameraAvailable, createQrDetector } from "./scanner-camera";

type Copy = (key: string, values?: Record<string, string | number>) => string;
type CameraState =
  "idle" | "requesting" | "active" | "denied" | "unsupported" | "error";

const cameraStatusKeys: Record<CameraState, string> = {
  idle: "merchant.scanner.cameraIdle",
  requesting: "merchant.scanner.cameraStarting",
  active: "merchant.scanner.ready",
  denied: "merchant.scanner.permissionTitle",
  unsupported: "merchant.scanner.cameraUnavailable",
  error: "merchant.scanner.cameraError",
};

export interface TillDeviceSession {
  paired: true;
  device: { id: string; label: string };
  merchant: { displayName: string };
  location: { id: string; displayName: string; timezone: string };
}

export interface MembershipProjection {
  id: string;
  merchant: {
    id: string;
    slug: string;
    displayName: string;
    shortDescription: string | null;
  };
  program: {
    id: string;
    version: number;
    type: "visit_stamp" | "item_stamp" | "spend_points";
    threshold: number;
    earnQuantity: number;
    name: string;
    description: string | null;
    rewardLabel: string;
    terms: string | null;
    itemGroups: Array<{ key: string; label: string }>;
    effectiveFrom: string;
  };
  balance: number;
  threshold: number;
  availableRewards: Array<{
    id: string;
    status: "available";
    label: string;
    issuedAt: string;
    expiresAt: string | null;
  }>;
  earnLimit: {
    dailyLimit: number;
    usedToday: number;
    remainingToday: number;
  } | null;
  appBoost: {
    status:
      "available_to_unlock" | "pending" | "fulfilled" | "expired" | "reversed";
    bonusQuantity: number;
    expiresAt: string | null;
    fundingSource: "collectie" | "merchant" | "shared";
  } | null;
  lastUpdatedAt: string;
}

export interface TransactionQuote {
  id: string;
  expiresAt: string;
  customer: { displayMemberCode: string };
  membership: MembershipProjection;
  allowedActions: Array<{
    type: "earn" | "redeem";
    labelKey: string;
    itemGroupKey?: string | null;
    rewardGrantId?: string | null;
  }>;
}

export interface TransactionReceipt {
  id: string;
  status: "committed";
  action: "earn" | "redeem" | "reverse";
  occurredAt: string;
  membership: MembershipProjection;
  ledgerEffects: Array<{
    type:
      | "earn"
      | "bonus"
      | "threshold_conversion"
      | "redeem"
      | "adjustment"
      | "expiry"
      | "reversal";
    delta: number;
    reasonCode: string;
  }>;
  rewardChanges: Array<{
    id: string;
    status: "available" | "redeemed" | "expired" | "revoked";
    label: string;
    issuedAt: string;
    expiresAt: string | null;
  }>;
  idempotentReplay: boolean;
}

export function earnedQuantity(receipt: TransactionReceipt): number {
  return receipt.ledgerEffects
    .filter((effect) => effect.type === "earn" || effect.type === "bonus")
    .reduce((total, effect) => total + Math.max(0, effect.delta), 0);
}

export type ScannerProblem =
  | "offline"
  | "card-unknown"
  | "quote-expired"
  | "reward-stale"
  | "device-revoked"
  | "action-unavailable"
  | "unexpected";

export type CommitAction =
  | { type: "earn"; itemGroupKey?: string }
  | { type: "redeem"; rewardGrantId: string };

export interface CommitAttempt {
  quote: TransactionQuote;
  idempotencyKey: string;
  body:
    | { quoteId: string; action: "earn" }
    | { quoteId: string; action: "redeem"; rewardGrantId: string };
}

export type ScannerState =
  | { status: "device-checking" }
  | { status: "device-error" }
  | { status: "unpaired"; error?: boolean }
  | { status: "ready"; camera: CameraState }
  | { status: "quoting" }
  | {
      status: "quote-error";
      reason: ScannerProblem;
      camera: CameraState;
    }
  | { status: "review"; quote: TransactionQuote }
  | { status: "committing"; attempt: CommitAttempt }
  | {
      status: "commit-error";
      attempt: CommitAttempt;
      reason: ScannerProblem;
    }
  | { status: "receipt"; receipt: TransactionReceipt };

export type ScannerAction =
  | { type: "device.paired" }
  | { type: "device.unpaired"; error?: boolean }
  | { type: "device.failed" }
  | { type: "camera.requesting" }
  | { type: "camera.active" }
  | { type: "camera.denied" }
  | { type: "camera.unsupported" }
  | { type: "camera.failed" }
  | { type: "quote.requested" }
  | { type: "quote.loaded"; quote: TransactionQuote }
  | {
      type: "quote.failed";
      reason: ScannerProblem;
      camera?: CameraState;
    }
  | { type: "commit.requested"; attempt: CommitAttempt }
  | { type: "commit.retry" }
  | {
      type: "commit.failed";
      reason: ScannerProblem;
    }
  | { type: "commit.succeeded"; receipt: TransactionReceipt }
  | { type: "reset" };

export function scannerReducer(
  state: ScannerState,
  action: ScannerAction,
): ScannerState {
  switch (action.type) {
    case "device.paired":
      return {
        status: "ready",
        camera: cameraAvailable() ? "idle" : "unsupported",
      };
    case "device.unpaired":
      return {
        status: "unpaired",
        ...(action.error ? { error: true } : {}),
      };
    case "device.failed":
      return { status: "device-error" };
    case "camera.requesting":
      return state.status === "ready"
        ? { status: "ready", camera: "requesting" }
        : state;
    case "camera.active":
      return state.status === "ready"
        ? { status: "ready", camera: "active" }
        : state;
    case "camera.denied":
      return { status: "ready", camera: "denied" };
    case "camera.unsupported":
      return { status: "ready", camera: "unsupported" };
    case "camera.failed":
      return { status: "ready", camera: "error" };
    case "quote.requested":
      return { status: "quoting" };
    case "quote.loaded":
      return { status: "review", quote: action.quote };
    case "quote.failed":
      return {
        status: "quote-error",
        reason: action.reason,
        camera: action.camera ?? "idle",
      };
    case "commit.requested":
      return { status: "committing", attempt: action.attempt };
    case "commit.retry":
      return state.status === "commit-error"
        ? { status: "committing", attempt: state.attempt }
        : state;
    case "commit.failed":
      return state.status === "committing"
        ? {
            status: "commit-error",
            attempt: state.attempt,
            reason: action.reason,
          }
        : state;
    case "commit.succeeded":
      return { status: "receipt", receipt: action.receipt };
    case "reset":
      return {
        status: "ready",
        camera: cameraAvailable() ? "idle" : "unsupported",
      };
  }
}

export function classifyScannerProblem(
  status: number,
  code: string,
): ScannerProblem {
  if (status === 0 || code === "NETWORK_ERROR") return "offline";
  if (status === 401) return "device-revoked";
  if (code === "CARD_CREDENTIAL_INVALID") return "card-unknown";
  if (code === "QUOTE_EXPIRED_OR_INVALID") return "quote-expired";
  if (code === "REWARD_UNAVAILABLE") return "reward-stale";
  if (code === "ACTION_NOT_ALLOWED") return "action-unavailable";
  return "unexpected";
}

function itemGroupLabel(quote: TransactionQuote, key: string): string {
  return (
    quote.membership.program.itemGroups.find((group) => group.key === key)
      ?.label ?? key
  );
}

export function createCommitAttempt(
  quote: TransactionQuote,
  idempotencyKey = `idem_scan_${crypto.randomUUID().replaceAll("-", "")}`,
  action: CommitAction = { type: "earn" },
): CommitAttempt {
  return {
    quote,
    idempotencyKey,
    body:
      action.type === "redeem"
        ? {
            quoteId: quote.id,
            action: "redeem",
            rewardGrantId: action.rewardGrantId,
          }
        : {
            quoteId: quote.id,
            action: "earn",
            ...(action.itemGroupKey
              ? { itemGroupKey: action.itemGroupKey }
              : {}),
          },
  };
}

function isQuote(value: unknown): value is TransactionQuote {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const quote = value as Record<string, unknown>;
  return (
    typeof quote.id === "string" &&
    typeof quote.expiresAt === "string" &&
    Boolean(quote.customer) &&
    typeof quote.customer === "object" &&
    Boolean(quote.membership) &&
    typeof quote.membership === "object" &&
    Array.isArray(quote.allowedActions)
  );
}

function isReceipt(value: unknown): value is TransactionReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const receipt = value as Record<string, unknown>;
  return (
    typeof receipt.id === "string" &&
    receipt.status === "committed" &&
    (receipt.action === "earn" || receipt.action === "redeem") &&
    Boolean(receipt.membership) &&
    typeof receipt.membership === "object" &&
    Array.isArray(receipt.ledgerEffects) &&
    Array.isArray(receipt.rewardChanges) &&
    typeof receipt.idempotentReplay === "boolean"
  );
}

function isDeviceSession(value: unknown): value is TillDeviceSession {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const session = value as Record<string, unknown>;
  const device = session.device as Record<string, unknown> | undefined;
  const merchant = session.merchant as Record<string, unknown> | undefined;
  const location = session.location as Record<string, unknown> | undefined;
  return (
    session.paired === true &&
    typeof device?.id === "string" &&
    typeof device.label === "string" &&
    typeof merchant?.displayName === "string" &&
    typeof location?.id === "string" &&
    typeof location.displayName === "string" &&
    typeof location.timezone === "string"
  );
}

async function loadDeviceSession(
  locale: Locale,
  signal?: AbortSignal,
): Promise<TillDeviceSession | null> {
  const response = await fetch("/v1/merchant/device-session", {
    credentials: "include",
    ...(signal ? { signal } : {}),
    headers: { accept: "application/json", "accept-language": locale },
  });
  if (!response.ok) throw new Error("device session unavailable");
  const value: unknown = await response.json();
  return isDeviceSession(value) ? value : null;
}

async function problemCode(response: Response): Promise<string> {
  const value: unknown = await response.json().catch(() => null);
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>).error &&
    typeof (value as Record<string, unknown>).error === "object" &&
    typeof ((value as Record<string, unknown>).error as Record<string, unknown>)
      .code === "string"
  ) {
    return ((value as Record<string, unknown>).error as { code: string }).code;
  }
  return "UNEXPECTED_RESPONSE";
}

function initialState(
  initialDeviceState: "paired" | "unpaired" | undefined,
): ScannerState {
  if (initialDeviceState === "paired") {
    return {
      status: "ready",
      camera: cameraAvailable() ? "idle" : "unsupported",
    };
  }
  if (initialDeviceState === "unpaired") return { status: "unpaired" };
  return { status: "device-checking" };
}

export function ScannerCheckout({
  locale,
  copy,
  initialDeviceState,
  // Server-rendered tests drive a specific till state; the browser always
  // starts from the device check.
  initialScannerState,
  onDeviceSession,
}: {
  locale: Locale;
  copy: Copy;
  initialDeviceState?: "paired" | "unpaired";
  initialScannerState?: ScannerState;
  onDeviceSession?: (session: TillDeviceSession | null) => void;
}) {
  const [state, setState] = useState<ScannerState>(
    () => initialScannerState ?? initialState(initialDeviceState),
  );
  const [pairingCode, setPairingCode] = useState("");
  const [pairing, setPairing] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const manualTokenRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanningRef = useRef(false);
  const cameraRequestRef = useRef(0);
  const cameraStartingRef = useRef(false);

  const dispatch = useCallback((action: ScannerAction) => {
    setState((current) => scannerReducer(current, action));
  }, []);

  const stopCamera = useCallback(() => {
    cameraRequestRef.current += 1;
    cameraStartingRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // Also invalidates permission requests that resolve after leaving the scanner.
  useEffect(() => () => stopCamera(), [stopCamera]);
  useEffect(() => {
    if (
      state.status !== "ready" ||
      (state.camera !== "requesting" && state.camera !== "active")
    ) {
      stopCamera();
    }
  }, [state, stopCamera]);

  useEffect(() => {
    if (initialDeviceState) return;
    const controller = new AbortController();
    loadDeviceSession(locale, controller.signal)
      .then((session) => {
        onDeviceSession?.(session);
        dispatch({ type: session ? "device.paired" : "device.unpaired" });
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          dispatch({ type: "device.failed" });
        }
      });
    return () => controller.abort();
  }, [dispatch, initialDeviceState, locale, onDeviceSession]);

  const requestQuote = useCallback(
    async (rawToken: string) => {
      const cardToken = rawToken.trim();
      if (cardToken.length < 24 || cardToken.length > 512) {
        dispatch({
          type: "quote.failed",
          reason: "card-unknown",
          camera: "unsupported",
        });
        return;
      }
      dispatch({ type: "quote.requested" });
      try {
        const response = await fetch("/v1/merchant/transaction-quotes", {
          method: "POST",
          credentials: "include",
          headers: {
            "accept-language": locale,
            "content-type": "application/json",
          },
          body: JSON.stringify({
            cardToken,
            requestedActionHint: "earn",
          }),
        });
        if (!response.ok) {
          const reason = classifyScannerProblem(
            response.status,
            await problemCode(response),
          );
          dispatch(
            reason === "device-revoked"
              ? { type: "device.unpaired", error: true }
              : { type: "quote.failed", reason },
          );
          return;
        }
        const value: unknown = await response.json();
        if (!isQuote(value)) {
          dispatch({ type: "quote.failed", reason: "unexpected" });
          return;
        }
        dispatch({ type: "quote.loaded", quote: value });
      } catch {
        dispatch({ type: "quote.failed", reason: "offline" });
      }
    },
    [dispatch, locale],
  );

  useEffect(() => {
    if (state.status !== "ready" || state.camera !== "active") return;
    const video = videoRef.current;
    if (!video) return;
    const detector = createQrDetector();
    let frame = 0;
    let stopped = false;
    let lastDetection = -Infinity;
    const detect = async (timestamp: number) => {
      if (stopped || scanningRef.current) return;
      if (timestamp - lastDetection < 100) {
        frame = requestAnimationFrame((time) => void detect(time));
        return;
      }
      lastDetection = timestamp;
      try {
        const results = await detector.detect(video);
        if (stopped || scanningRef.current) return;
        const value = results.find((result) => result.rawValue)?.rawValue;
        if (value) {
          scanningRef.current = true;
          await requestQuote(value);
        }
      } catch {
        if (stopped) return;
        stopCamera();
        dispatch({ type: "camera.failed" });
        return;
      }
      if (!stopped) frame = requestAnimationFrame((time) => void detect(time));
    };
    frame = requestAnimationFrame((time) => void detect(time));
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
    };
  }, [dispatch, requestQuote, state, stopCamera]);

  useEffect(() => {
    if (state.status === "ready") scanningRef.current = false;
  }, [state.status]);

  const startCamera = async () => {
    if (
      state.status !== "ready" ||
      cameraStartingRef.current ||
      streamRef.current
    )
      return;
    if (!cameraAvailable()) {
      dispatch({ type: "camera.unsupported" });
      return;
    }
    const request = ++cameraRequestRef.current;
    cameraStartingRef.current = true;
    dispatch({ type: "camera.requesting" });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      if (request !== cameraRequestRef.current || !videoRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      if (request !== cameraRequestRef.current) return;
      dispatch({ type: "camera.active" });
    } catch (error: unknown) {
      if (request !== cameraRequestRef.current) return;
      stopCamera();
      dispatch({
        type:
          error instanceof DOMException && error.name === "NotAllowedError"
            ? "camera.denied"
            : "camera.failed",
      });
    } finally {
      if (request === cameraRequestRef.current)
        cameraStartingRef.current = false;
    }
  };

  const submitPairing = async (event: FormEvent) => {
    event.preventDefault();
    setPairing(true);
    try {
      const response = await fetch("/v1/merchant/device-pairings", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ pairingCode }),
      });
      if (!response.ok) {
        dispatch({ type: "device.unpaired", error: true });
        return;
      }
      const value: unknown = await response.json();
      if (
        !value ||
        typeof value !== "object" ||
        (value as Record<string, unknown>).paired !== true
      ) {
        dispatch({ type: "device.unpaired", error: true });
        return;
      }
      setPairingCode("");
      dispatch({ type: "device.paired" });
      loadDeviceSession(locale)
        .then((session) => onDeviceSession?.(session))
        .catch(() => onDeviceSession?.(null));
    } catch {
      dispatch({ type: "device.unpaired", error: true });
    } finally {
      setPairing(false);
    }
  };

  const submitManual = (event: FormEvent) => {
    event.preventDefault();
    const input = manualTokenRef.current;
    const value = input?.value ?? "";
    if (input) input.value = "";
    setManualOpen(false);
    scanningRef.current = true;
    void requestQuote(value);
  };

  const commit = useCallback(
    async (attempt: CommitAttempt) => {
      try {
        const response = await fetch("/v1/merchant/transactions", {
          method: "POST",
          credentials: "include",
          headers: {
            "accept-language": locale,
            "content-type": "application/json",
            "idempotency-key": attempt.idempotencyKey,
          },
          body: JSON.stringify(attempt.body),
        });
        if (!response.ok) {
          const reason = classifyScannerProblem(
            response.status,
            await problemCode(response),
          );
          dispatch({ type: "commit.failed", reason });
          return;
        }
        const value: unknown = await response.json();
        dispatch(
          isReceipt(value)
            ? { type: "commit.succeeded", receipt: value }
            : { type: "commit.failed", reason: "unexpected" },
        );
      } catch {
        dispatch({ type: "commit.failed", reason: "offline" });
      }
    },
    [dispatch, locale],
  );

  const confirmEarn = (quote: TransactionQuote, itemGroupKey?: string) => {
    const allowed = quote.allowedActions.some(
      (action) =>
        action.type === "earn" &&
        (action.itemGroupKey ?? undefined) === itemGroupKey,
    );
    if (!allowed) {
      dispatch({
        type: "quote.failed",
        reason: "action-unavailable",
      });
      return;
    }
    const attempt = createCommitAttempt(quote, undefined, {
      type: "earn",
      ...(itemGroupKey ? { itemGroupKey } : {}),
    });
    dispatch({ type: "commit.requested", attempt });
    void commit(attempt);
  };

  const confirmRedemption = (
    quote: TransactionQuote,
    rewardGrantId: string,
  ) => {
    const allowed = quote.allowedActions.some(
      (action) =>
        action.type === "redeem" && action.rewardGrantId === rewardGrantId,
    );
    const available = quote.membership.availableRewards.some(
      (reward) => reward.id === rewardGrantId,
    );
    if (!allowed || !available) {
      dispatch({
        type: "quote.failed",
        reason: "action-unavailable",
      });
      return;
    }
    const attempt = createCommitAttempt(quote, undefined, {
      type: "redeem",
      rewardGrantId,
    });
    dispatch({ type: "commit.requested", attempt });
    void commit(attempt);
  };

  const retryCommit = (attempt: CommitAttempt) => {
    dispatch({ type: "commit.retry" });
    void commit(attempt);
  };

  if (state.status === "device-checking") {
    return (
      <ScannerStatus
        busy
        title={copy("merchant.scanner.deviceChecking")}
        body={copy("merchant.scanner.deviceCheckingBody")}
      />
    );
  }
  if (state.status === "device-error") {
    return (
      <ScannerStatus
        title={copy("merchant.scanner.deviceCheckError")}
        body={copy("merchant.scanner.offlineBody")}
        action={copy("common.retry")}
        onAction={() => window.location.reload()}
      />
    );
  }
  if (state.status === "unpaired") {
    return (
      <section className="scanner-card pairing-card">
        <p className="eyebrow">{copy("merchant.scanner.deviceScope")}</p>
        <h1>{copy("merchant.scanner.pairTitle")}</h1>
        <p>{copy("merchant.scanner.pairBody")}</p>
        <form onSubmit={submitPairing}>
          <label htmlFor="pairing-code">
            {copy("merchant.scanner.pairingCode")}
          </label>
          <input
            id="pairing-code"
            className="code-input"
            value={pairingCode}
            inputMode="text"
            autoComplete="one-time-code"
            minLength={8}
            maxLength={8}
            pattern="[A-Z2-9]{8}"
            onChange={(event) =>
              setPairingCode(
                event.target.value
                  .toUpperCase()
                  .replace(/[^A-Z2-9]/g, "")
                  .slice(0, 8),
              )
            }
            required
          />
          <p className="field-help">{copy("merchant.scanner.pairHelp")}</p>
          {state.error && (
            <p className="form-error" role="alert">
              {copy("merchant.scanner.pairError")}
            </p>
          )}
          <button
            className="primary-button"
            type="submit"
            disabled={pairing || pairingCode.length !== 8}
          >
            {copy(
              pairing
                ? "merchant.scanner.pairing"
                : "merchant.scanner.pairAction",
            )}
          </button>
        </form>
      </section>
    );
  }
  if (state.status === "quoting") {
    return (
      <ScannerStatus
        busy
        title={copy("merchant.scanner.checking")}
        body={copy("merchant.scanner.noValueYet")}
      />
    );
  }
  if (state.status === "quote-error") {
    return (
      <ScannerStatus
        title={copy(
          state.reason === "card-unknown"
            ? "merchant.scanner.cardUnknownTitle"
            : state.reason === "quote-expired"
              ? "merchant.scanner.quoteExpiredTitle"
              : state.reason === "offline"
                ? "merchant.scanner.offlineTitle"
                : "merchant.scanner.actionErrorTitle",
        )}
        body={copy(
          state.reason === "card-unknown"
            ? "merchant.scanner.cardUnknownBody"
            : state.reason === "quote-expired"
              ? "merchant.scanner.quoteExpiredBody"
              : state.reason === "offline"
                ? "merchant.scanner.offlineBody"
                : "merchant.scanner.actionErrorBody",
        )}
        action={copy("merchant.scanner.scanAgain")}
        onAction={() => dispatch({ type: "reset" })}
      />
    );
  }
  if (state.status === "review") {
    const { quote } = state;
    const redeemableRewards = quote.allowedActions.flatMap((action) => {
      if (action.type !== "redeem" || !action.rewardGrantId) return [];
      const reward = quote.membership.availableRewards.find(
        (candidate) => candidate.id === action.rewardGrantId,
      );
      return reward ? [reward] : [];
    });
    // The server decides which stamps this scan may add; the till only renders
    // what it was offered.
    const itemGroupActions = quote.allowedActions.filter(
      (action) => action.type === "earn" && action.itemGroupKey,
    );
    const earnLimitReached =
      !quote.allowedActions.some((action) => action.type === "earn") &&
      (quote.membership.earnLimit?.remainingToday ?? null) === 0;
    return (
      <section className="scanner-card review-card">
        <p className="eyebrow">{copy("merchant.scanner.confirmLabel")}</p>
        <h1>{quote.customer.displayMemberCode}</h1>
        <p>{quote.membership.program.name}</p>
        <div className="balance-review">
          <span>{copy("merchant.scanner.currentBalance")}</span>
          <strong>
            {new Intl.NumberFormat(locale).format(quote.membership.balance)}
            <small>
              /
              {new Intl.NumberFormat(locale).format(quote.membership.threshold)}
            </small>
          </strong>
        </div>
        <p className="quote-expiry">{copy("merchant.scanner.quoteWindow")}</p>
        {redeemableRewards.length > 0 && (
          <div className="redeemable-rewards">
            <p className="eyebrow">
              {copy("merchant.scanner.availableRewards")}
            </p>
            {redeemableRewards.map((reward) => (
              <div className="redeemable-reward" key={reward.id}>
                <div>
                  <strong>{reward.label}</strong>
                  <span>{copy("merchant.scanner.rewardReady")}</span>
                </div>
                <button
                  className="quiet-button"
                  type="button"
                  onClick={() => confirmRedemption(quote, reward.id)}
                >
                  {copy("merchant.scanner.confirmRedeem")}
                </button>
              </div>
            ))}
          </div>
        )}
        {earnLimitReached && (
          <p className="scanner-note" role="status">
            {copy("merchant.scanner.dailyLimitReached", {
              limit: quote.membership.earnLimit?.dailyLimit ?? 0,
            })}
          </p>
        )}
        <div className="scanner-actions">
          <button
            className="quiet-button"
            type="button"
            onClick={() => dispatch({ type: "reset" })}
          >
            {copy("common.cancel")}
          </button>
          {!earnLimitReached &&
            (itemGroupActions.length > 0 ? (
              <div className="item-group-actions">
                <p className="eyebrow">
                  {copy("merchant.scanner.chooseItemGroup")}
                </p>
                {itemGroupActions.map((action) => (
                  <button
                    className="primary-button"
                    key={action.itemGroupKey}
                    type="button"
                    onClick={() => confirmEarn(quote, action.itemGroupKey!)}
                  >
                    {copy("merchant.scanner.confirmEarnItem", {
                      item: itemGroupLabel(quote, action.itemGroupKey!),
                      quantity: quote.membership.program.earnQuantity,
                    })}
                  </button>
                ))}
              </div>
            ) : (
              <button
                className="primary-button"
                type="button"
                onClick={() => confirmEarn(quote)}
              >
                {copy("merchant.scanner.confirmEarn", {
                  quantity: quote.membership.program.earnQuantity,
                })}
              </button>
            ))}
        </div>
      </section>
    );
  }
  if (state.status === "committing") {
    return (
      <ScannerStatus
        busy
        title={copy("merchant.scanner.committing")}
        body={copy("merchant.scanner.commitPending")}
      />
    );
  }
  if (state.status === "commit-error") {
    return (
      <ScannerStatus
        title={copy(
          state.reason === "quote-expired"
            ? "merchant.scanner.quoteExpiredTitle"
            : state.reason === "reward-stale"
              ? "merchant.scanner.rewardStaleTitle"
              : state.reason === "offline"
                ? "merchant.scanner.offlineTitle"
                : "merchant.scanner.actionErrorTitle",
        )}
        body={copy(
          state.reason === "quote-expired"
            ? "merchant.scanner.quoteExpiredBody"
            : state.reason === "reward-stale"
              ? "merchant.scanner.rewardStaleBody"
              : state.reason === "offline"
                ? "merchant.scanner.offlineBody"
                : "merchant.scanner.actionErrorBody",
        )}
        action={copy("merchant.scanner.retrySameAction")}
        onAction={() => retryCommit(state.attempt)}
        secondaryAction={copy("merchant.scanner.scanAgain")}
        onSecondaryAction={() => dispatch({ type: "reset" })}
      />
    );
  }
  if (state.status === "receipt") {
    const redeemed = state.receipt.action === "redeem";
    const quantity = earnedQuantity(state.receipt);
    const rewardUnlocked = state.receipt.rewardChanges.some(
      (reward) => reward.status === "available",
    );
    return (
      <section className="scanner-card receipt-card" aria-live="polite">
        <div className="success-mark" aria-hidden="true">
          ✓
        </div>
        <p className="eyebrow">{copy("merchant.scanner.success")}</p>
        <h1>
          {redeemed
            ? copy("merchant.scanner.rewardRedeemed")
            : copy("merchant.scanner.balanceNow", {
                balance: new Intl.NumberFormat(locale).format(
                  state.receipt.membership.balance,
                ),
              })}
        </h1>
        <p>
          {redeemed
            ? copy("merchant.scanner.redeemedReceipt")
            : copy(
                rewardUnlocked
                  ? "merchant.scanner.earnedRewardReceipt"
                  : "merchant.scanner.earnedReceipt",
                {
                  quantity: new Intl.NumberFormat(locale).format(quantity),
                },
              )}
        </p>
        {state.receipt.idempotentReplay && (
          <p className="duplicate-note">{copy("merchant.scanner.duplicate")}</p>
        )}
        <button
          className="primary-button"
          type="button"
          onClick={() => dispatch({ type: "reset" })}
        >
          {copy("merchant.scanner.nextCustomer")}
        </button>
      </section>
    );
  }

  return (
    <section className="scanner-workspace">
      <div
        className={`scan-frame camera-${state.camera}`}
        aria-label={copy("merchant.scanner.cameraHelp")}
      >
        <video ref={videoRef} muted playsInline aria-hidden="true" />
        <span className="scan-line" aria-hidden="true" />
      </div>
      <p className="scanner-status" role="status">
        <span aria-hidden="true" />
        {copy(cameraStatusKeys[state.camera])}
      </p>
      <h1>
        {copy(
          state.camera === "denied"
            ? "merchant.scanner.permissionTitle"
            : "merchant.scanner.prompt",
        )}
      </h1>
      <p>
        {state.camera === "denied"
          ? copy("merchant.scanner.permissionBody")
          : state.camera === "unsupported"
            ? copy("merchant.scanner.cameraUnavailableBody")
            : state.camera === "error"
              ? copy("merchant.scanner.cameraErrorBody")
              : state.camera === "idle" || state.camera === "requesting"
                ? copy("merchant.scanner.cameraPermissionHelp")
                : copy("merchant.scanner.hint")}
      </p>
      <div className="scanner-actions">
        {state.camera !== "active" && (
          <button
            className="primary-button"
            type="button"
            disabled={state.camera === "requesting"}
            onClick={() => void startCamera()}
          >
            {copy("merchant.scanner.enableCamera")}
          </button>
        )}
        <button
          className="quiet-button"
          type="button"
          onClick={() => setManualOpen((current) => !current)}
        >
          {copy("merchant.scanner.manual")}
        </button>
      </div>
      {manualOpen && (
        <form className="manual-card-form" onSubmit={submitManual}>
          <label htmlFor="manual-card-token">
            {copy("merchant.scanner.manualLabel")}
          </label>
          <input
            ref={manualTokenRef}
            id="manual-card-token"
            type="password"
            autoComplete="off"
            minLength={24}
            maxLength={512}
            required
          />
          <p className="field-help">{copy("merchant.scanner.manualHelp")}</p>
          <button className="primary-button compact" type="submit">
            {copy("merchant.scanner.checkCustomer")}
          </button>
        </form>
      )}
    </section>
  );
}

function ScannerStatus({
  title,
  body,
  busy = false,
  action,
  onAction,
  secondaryAction,
  onSecondaryAction,
}: {
  title: string;
  body: string;
  busy?: boolean;
  action?: string;
  onAction?: () => void;
  secondaryAction?: string;
  onSecondaryAction?: () => void;
}) {
  return (
    <section
      className="scanner-card status-card scanner-flow-status"
      aria-busy={busy}
      role={busy ? "status" : "alert"}
    >
      {busy && <div className="loading-mark" aria-hidden="true" />}
      <h1>{title}</h1>
      <p>{body}</p>
      {(action || secondaryAction) && (
        <div className="scanner-actions">
          {secondaryAction && onSecondaryAction && (
            <button
              className="quiet-button"
              type="button"
              onClick={onSecondaryAction}
            >
              {secondaryAction}
            </button>
          )}
          {action && onAction && (
            <button className="primary-button" type="button" onClick={onAction}>
              {action}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
